import { clsx } from "clsx";
import React from "react";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import * as styles from "./text-input.css";

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    id: string;
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    hintText?: string;
    errorMessage?: string;
    icon?: SVGIconName;
    autoComplete?: string;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    (
        {
            id,
            name,
            label,
            placeholder,
            required = false,
            error = false,
            hintText,
            errorMessage,
            icon,
            autoComplete = "on",
            ...inputProps
        },
        ref
    ): JSX.Element => {
        return (
            <div className={styles.container}>
                <div className={styles.inputWrapper}>
                    <input
                        type="text"
                        id={id}
                        name={name}
                        className={clsx(styles.input, {
                            [styles.error]: error,
                            [styles.withIcon]: icon,
                        })}
                        placeholder={placeholder || label}
                        required={required}
                        aria-required={required}
                        aria-invalid={error}
                        aria-describedby={error ? `${id}-error` : undefined}
                        autoComplete={autoComplete}
                        ref={ref}
                        {...inputProps}
                    />
                    {icon && <SVGIcon name={icon} className={styles.icon} />}
                    <label htmlFor={id} className={styles.label}>
                        {label}
                    </label>
                </div>
                {hintText && !error && (
                    <Text as="p" textStyle="body-xs" color="secondary">
                        {hintText}
                    </Text>
                )}
                {error && errorMessage && (
                    <div id={`${id}-error`} aria-live="polite">
                        <Text as="p" textStyle="body-xs" color="error">
                            {errorMessage}
                        </Text>
                    </div>
                )}
            </div>
        );
    }
);

TextInput.displayName = "TextInput";
